import { useEffect, useMemo, useState } from 'react'
import { Shield, Trash2, Plus, Lock, Unlock, Check, X } from 'lucide-react'
import { managedDeviceFirewallApi } from '../../../../services/managedDeviceFirewallApi'

const DIRECTIONS = ['Input', 'Output']
const PROTOCOLS = ['Any', 'Tcp', 'Udp', 'Icmp']
const ACTIONS = ['Accept', 'Drop']
const PEER_SOURCE_TYPES = ['Any', 'IpOrCidr', 'TenantRoleGroup', 'AdGroup']
const TARGET_SCOPES = ['DeviceItself', 'RemoteNetworkCidr', 'SpecificRemoteIp']

const PEER_SOURCE_LABEL = {
  Any: 'Qualquer',
  IpOrCidr: 'IP/CIDR',
  TenantRoleGroup: 'Grupo do Tenant',
  AdGroup: 'Grupo do AD',
}
const TARGET_SCOPE_LABEL = {
  DeviceItself: 'Este device',
  RemoteNetworkCidr: 'LAN inteira (CIDR)',
  SpecificRemoteIp: 'IP específico atrás',
}

const emptyForm = {
  Action: 'Accept',
  Protocol: 'Any',
  PortStart: '',
  PortEnd: '',
  PeerSourceType: 'Any',
  PeerSourceValue: '',
  TargetScope: 'DeviceItself',
  TargetValue: '',
  Comment: '',
  Enabled: true,
}

export default function FirewallSection({ device }) {
  const deviceId = device?.id
  const [direction, setDirection] = useState('Input')
  const [rules, setRules] = useState([])
  const [outputDefault, setOutputDefault] = useState(device?.firewallOutputDefaultAction || 'Accept')
  const [sources, setSources] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const loadRules = async () => {
    if (!deviceId) return
    setLoading(true)
    setError(null)
    try {
      const data = await managedDeviceFirewallApi.listRules(deviceId, direction)
      setRules(data)
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    } finally {
      setLoading(false)
    }
  }

  const loadSources = async () => {
    if (!deviceId) return
    try {
      const data = await managedDeviceFirewallApi.getAvailableSources(deviceId)
      setSources(data)
    } catch (e) {
      /* silencioso: sources é opcional */
    }
  }

  useEffect(() => { loadRules() }, [deviceId, direction]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSources() }, [deviceId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    setError(null)
    try {
      const body = {
        direction,
        action: form.Action,
        protocol: form.Protocol,
        portStart: form.PortStart === '' ? null : parseInt(form.PortStart, 10),
        portEnd: form.PortEnd === '' ? null : parseInt(form.PortEnd, 10),
        peerSourceType: form.PeerSourceType,
        peerSourceValue: form.PeerSourceValue || null,
        targetScope: form.TargetScope,
        targetValue: form.TargetValue || null,
        comment: form.Comment || null,
        enabled: form.Enabled,
      }
      await managedDeviceFirewallApi.createRule(deviceId, body)
      setForm(emptyForm)
      setShowForm(false)
      await loadRules()
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    }
  }

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Remover esta regra?')) return
    try {
      await managedDeviceFirewallApi.deleteRule(deviceId, ruleId)
      await loadRules()
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    }
  }

  const handleToggleOutputDefault = async () => {
    const next = outputDefault === 'Accept' ? 'Drop' : 'Accept'
    try {
      const res = await managedDeviceFirewallApi.setOutputDefault(deviceId, next)
      setOutputDefault(res.action)
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    }
  }

  const autoInputBanner = useMemo(() => ([
    { label: 'localhost (console/VNC/WebRTC)', icon: '🟢' },
    { label: 'Tenant Owner', icon: '🟢' },
    { label: 'Tenant Admin', icon: '🟢' },
  ]), [])

  return (
    <section id="firewall" className="bg-white border border-gray-200 rounded-lg">
      <header className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-theme-primary" />
          <h2 className="font-medium text-gray-900">Firewall do Device</h2>
          <span className="text-xs text-gray-500">(nftables)</span>
        </div>
        <button
          onClick={() => { setShowForm(true); setForm(emptyForm) }}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded hover:bg-theme-primary/90"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar regra
        </button>
      </header>

      <div className="flex border-b border-gray-200">
        {DIRECTIONS.map(d => (
          <button
            key={d}
            onClick={() => setDirection(d)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
              direction === d
                ? 'border-theme-primary text-theme-primary font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {d === 'Input' ? 'Input (entrada)' : 'Output (saída)'}
          </button>
        ))}
      </div>

      {direction === 'Input' && (
        <div className="bg-amber-50 border-b border-amber-200 p-3 text-xs text-amber-900">
          <div className="font-medium mb-1 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Default: DROP (bloqueia tudo que não casar com regra)
          </div>
          <div>Regras automáticas (não editáveis):</div>
          <ul className="ml-4 mt-1 list-disc">
            {autoInputBanner.map(x => <li key={x.label}>{x.icon} {x.label}</li>)}
          </ul>
        </div>
      )}

      {direction === 'Output' && (
        <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 p-3 text-xs">
          <div className="flex items-center gap-2">
            {outputDefault === 'Accept' ? <Unlock className="w-3 h-3 text-green-600" /> : <Lock className="w-3 h-3 text-red-600" />}
            <span className="text-gray-700">
              Default action do output: <strong>{outputDefault === 'Accept' ? 'ALLOW ALL' : 'DENY ALL'}</strong>
            </span>
          </div>
          <button
            onClick={handleToggleOutputDefault}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"
          >
            Trocar para {outputDefault === 'Accept' ? 'DENY ALL' : 'ALLOW ALL'}
          </button>
        </div>
      )}

      {error && (
        <div className="m-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
          {error}
        </div>
      )}

      {showForm && (
        <RuleForm
          form={form}
          setForm={setForm}
          sources={sources}
          onCancel={() => { setShowForm(false); setForm(emptyForm) }}
          onSubmit={handleCreate}
          direction={direction}
        />
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Ação</th>
              <th className="px-3 py-2 text-left">Protocolo / Porta</th>
              <th className="px-3 py-2 text-left">{direction === 'Input' ? 'Origem' : 'Destino'}</th>
              <th className="px-3 py-2 text-left">{direction === 'Input' ? 'Alvo do device' : 'Origem do device'}</th>
              <th className="px-3 py-2 text-left">Comentário</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={7} className="text-center py-4 text-gray-400">Carregando...</td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-gray-400">Nenhuma regra custom — só as automáticas atuam.</td></tr>
            )}
            {rules.map(r => (
              <tr key={r.id} className={r.enabled ? '' : 'opacity-50'}>
                <td className="px-3 py-2 text-xs text-gray-500 font-mono">{r.position}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${r.action === 'Accept' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {r.action}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs font-mono">
                  {r.protocol}
                  {r.portStart != null && (
                    <span className="ml-1">
                      :{r.portStart}{r.portEnd != null && r.portEnd !== r.portStart ? `-${r.portEnd}` : ''}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="text-gray-500">{PEER_SOURCE_LABEL[r.peerSourceType]}</span>
                  {r.peerSourceValue && <span className="ml-1 font-mono">{r.peerSourceLabel || r.peerSourceValue}</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="text-gray-500">{TARGET_SCOPE_LABEL[r.targetScope]}</span>
                  {r.targetValue && <span className="ml-1 font-mono">{r.targetValue}</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.comment || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => handleDelete(r.id)} title="Remover" className="p-1 text-red-600 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function RuleForm({ form, setForm, sources, onCancel, onSubmit, direction }) {
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))
  const portsApplicable = form.Protocol === 'Tcp' || form.Protocol === 'Udp'

  return (
    <div className="p-4 bg-theme-primary/10 border-b border-theme-primary/30 grid grid-cols-2 md:grid-cols-4 gap-3">
      <Select label="Ação" value={form.Action} onChange={v => set('Action', v)} options={ACTIONS} />
      <Select label="Protocolo" value={form.Protocol} onChange={v => set('Protocol', v)} options={PROTOCOLS} />
      <Input label="Porta inicial" type="number" value={form.PortStart} onChange={v => set('PortStart', v)} disabled={!portsApplicable} />
      <Input label="Porta final (range)" type="number" value={form.PortEnd} onChange={v => set('PortEnd', v)} disabled={!portsApplicable} />

      <Select
        label={direction === 'Input' ? 'Tipo da origem' : 'Tipo do destino'}
        value={form.PeerSourceType}
        onChange={v => set('PeerSourceType', v)}
        options={PEER_SOURCE_TYPES}
        labelMap={PEER_SOURCE_LABEL}
      />
      <PeerSourceValueField
        type={form.PeerSourceType}
        value={form.PeerSourceValue}
        onChange={v => set('PeerSourceValue', v)}
        sources={sources}
      />

      <Select
        label="Alvo no device"
        value={form.TargetScope}
        onChange={v => set('TargetScope', v)}
        options={TARGET_SCOPES}
        labelMap={TARGET_SCOPE_LABEL}
      />
      <TargetValueField
        scope={form.TargetScope}
        value={form.TargetValue}
        onChange={v => set('TargetValue', v)}
        remoteNetworks={sources?.remoteNetworks || []}
      />

      <div className="col-span-2 md:col-span-4">
        <Input label="Comentário" value={form.Comment} onChange={v => set('Comment', v)} />
      </div>

      <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-white">
          <X className="inline w-3.5 h-3.5 mr-1" /> Cancelar
        </button>
        <button onClick={onSubmit} className="px-3 py-1.5 text-sm bg-theme-primary text-white rounded hover:bg-theme-primary/90">
          <Check className="inline w-3.5 h-3.5 mr-1" /> Criar regra
        </button>
      </div>
    </div>
  )
}

function Select({ label, value, onChange, options, labelMap, disabled }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full text-sm border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100"
      >
        {options.map(o => (
          <option key={o} value={o}>{labelMap?.[o] || o}</option>
        ))}
      </select>
    </div>
  )
}

function Input({ label, value, onChange, type = 'text', disabled }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100"
      />
    </div>
  )
}

function PeerSourceValueField({ type, value, onChange, sources }) {
  if (type === 'Any') return <div />
  if (type === 'TenantRoleGroup') {
    return (
      <div>
        <label className="block text-xs text-gray-600 mb-1">Role</label>
        <select value={value} onChange={e => onChange(e.target.value)} className="w-full text-sm border border-gray-300 rounded px-2 py-1">
          <option value="">-- selecione --</option>
          {(sources?.tenantRoles || []).map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>
    )
  }
  if (type === 'AdGroup') {
    return (
      <div>
        <label className="block text-xs text-gray-600 mb-1">Grupo AD</label>
        <select value={value} onChange={e => onChange(e.target.value)} className="w-full text-sm border border-gray-300 rounded px-2 py-1">
          <option value="">-- selecione --</option>
          {(sources?.adGroups || []).map(g => (
            <option key={g.objectGuid} value={g.objectGuid}>{g.displayName}</option>
          ))}
        </select>
      </div>
    )
  }
  return <Input label="IP ou CIDR" value={value} onChange={onChange} />
}

function TargetValueField({ scope, value, onChange, remoteNetworks }) {
  if (scope === 'DeviceItself') return <div />
  if (scope === 'RemoteNetworkCidr') {
    return (
      <div>
        <label className="block text-xs text-gray-600 mb-1">CIDR da RemoteNetwork</label>
        <select value={value} onChange={e => onChange(e.target.value)} className="w-full text-sm border border-gray-300 rounded px-2 py-1">
          <option value="">-- selecione --</option>
          {remoteNetworks.map(rn => (
            <option key={rn.cidr} value={rn.cidr}>{rn.cidr}{rn.description ? ` — ${rn.description}` : ''}</option>
          ))}
        </select>
      </div>
    )
  }
  return <Input label="IP específico (dentro de alguma RemoteNetwork)" value={value} onChange={onChange} />
}
